import { Switch } from '@headlessui/react'
import styled from 'styled-components'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'

type SwitchProps = Parameters<typeof Switch>[0]

const TypedSwitch = (props: SwitchProps) => <Switch as="button" {...props} />

const StyledSwitch = styled(TypedSwitch)<{ $checked: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 24px;
  width: 44px;
  padding: 0;
  border: ${({ theme, $checked }) =>
    $checked ? 'none' : `1px solid ${theme.colors.button.secondaryBorder}`};
  border-radius: 34px;
  cursor: pointer;
  background-color: ${({ theme, $checked }) =>
    $checked ? theme.colors.button.primary : theme.colors.button.secondary};
  transition: background-color 0.2s ease-in-out;

  &:focus {
    outline: none;
  }
`

const Slider = styled.span<{ $checked: boolean }>`
  position: absolute;
  height: 20px;
  width: 20px;
  left: 2px;
  border: ${({ theme }) => `1px solid ${theme.colors.button.secondaryBorder}`};
  border-radius: 50%;
  background-color: white;
  transition: transform 0.2s ease-in-out;
  transform: ${({ $checked }) =>
    $checked ? 'translateX(20px)' : 'translateX(0)'};
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 1em;
  width: fit-content;

  > label {
    color: ${({ theme }) => theme.colors.font.regular};
    font-size: var(--small-text);
    font-weight: 700;
  }
`

interface Props extends Omit<SwitchProps, 'checked' | 'onChange'> {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: React.ReactNode
  className?: string
}

export const Toggle: React.FC<Props> = ({
  checked,
  onChange,
  label,
  className,
  id,
  ...props
}) => {
  const toggleId = useMemo(() => id || `toggle-${nanoid()}`, [id])

  return (
    <Container className={className}>
      {label && <label htmlFor={toggleId}>{label}</label>}
      <StyledSwitch
        id={toggleId}
        checked={checked}
        onChange={onChange}
        $checked={checked}
        {...props}
      >
        <Slider $checked={checked} />
      </StyledSwitch>
    </Container>
  )
}
